import { useCallback } from "react";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type {
  MotionAlignmentConfig,
  MotionAnchorPositionConfig,
  MotionColorInput,
  MotionColorReference,
  MotionTargetQuery,
} from "@blings/blings-player/editor-tools";
import { hexToRgba, rgbaArrToHexStr } from "../../../helpers/colors";
import { MIN_SCENE_DURATION_FRAMES } from "../../TimelinePanel/utils/sceneTrimConstants";
import { useAnimationJsonEdit } from "../../TimelinePanel/hooks/useAnimationJsonEdit";
import type {
  ApplyDocumentDuration,
  ApplyDocumentSize,
  DocumentColor,
  ApplyLayerFont,
  ApplyAnchorPosition,
  ReplaceDocumentColor,
  ReplaceDocumentMedia,
  ReplaceMedia,
  RemoveFieldValue,
  VisualEditableField,
} from "./types";
import {
  getPendingShapeFillOptions,
  getPendingShapeOutlineOptions,
  removeShapeStyleItem,
} from "./shapeStyleEdits";
import {
  buildFieldTarget,
  getPendingFieldPath,
  getTextDocumentMutationProperty,
  isPendingField,
} from "./utils";

type UseLayerVisualPropertyActionsConfig = {
  getCurrentFrame: () => number;
  target: MotionTargetQuery | null;
  targets?: MotionTargetQuery[];
};

const TEXT_DOCUMENT_COLOR_PATH_PATTERN = /^t\.d\.k\.(\d+)\.s\.(fc|sc)$/;
const DOCUMENT_SIZE_FIELD_BY_DIMENSION = {
  width: "w",
  height: "h",
} as const;
type RgbaColor = [number, number, number, number];

const normalizeDocumentColorPath = (path: string): string =>
  path.replace(/\[(\d+)\]/g, ".$1").replace(/^\./, "");

const getPathSegments = (path: string): string[] =>
  normalizeDocumentColorPath(path).split(".").filter(Boolean);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function getPathValue(root: unknown, path: string): unknown {
  return getPathSegments(path).reduce<unknown>((current, segment) => {
    if (!isRecord(current)) {
      return undefined;
    }

    return current[segment];
  }, root);
}

function setPathValue(root: unknown, path: string, value: unknown): boolean {
  const segments = getPathSegments(path);
  const key = segments.pop();
  if (!key) {
    return false;
  }

  const parent = segments.reduce<unknown>((current, segment) => {
    if (!isRecord(current)) {
      return undefined;
    }

    return current[segment];
  }, root);

  if (!isRecord(parent) || !(key in parent)) {
    return false;
  }

  const previousValue = parent[key];
  if (JSON.stringify(previousValue) === JSON.stringify(value)) {
    return false;
  }

  parent[key] = value;
  return true;
}

const clampUnit = (value: number): number => Math.min(1, Math.max(0, value));

const normalizeColorChannel = (value: number): number =>
  value > 1 ? clampUnit(value / 255) : clampUnit(value);

const roundColorChannel = (value: number): number =>
  Number(clampUnit(value).toFixed(6));

const toColorByte = (value: number): number =>
  Math.round(clampUnit(value) * 255);

function parseDocumentReplacementColor(
  value: MotionColorInput
): RgbaColor | undefined {
  if (Array.isArray(value) && value.length >= 3) {
    return [
      normalizeColorChannel(value[0]),
      normalizeColorChannel(value[1]),
      normalizeColorChannel(value[2]),
      normalizeColorChannel(value[3] ?? 1),
    ];
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (/^#?([a-f\d]{3,4}|[a-f\d]{6}|[a-f\d]{8})$/i.test(trimmedValue)) {
    return hexToRgba(trimmedValue);
  }

  const rgbMatch = /^rgba?\((.*)\)$/i.exec(trimmedValue);
  if (!rgbMatch) {
    return undefined;
  }

  const parts = rgbMatch[1]
    .replace(/\s*\/\s*/, ",")
    .split(rgbMatch[1].includes(",") ? "," : /\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) {
    return undefined;
  }

  const rgba: RgbaColor = [
    normalizeColorChannel(Number(parts[0])),
    normalizeColorChannel(Number(parts[1])),
    normalizeColorChannel(Number(parts[2])),
    normalizeColorChannel(Number(parts[3] ?? 1)),
  ];

  return rgba.some((channel) => Number.isNaN(channel)) ? undefined : rgba;
}

function formatDocumentColorReplacement(
  reference: MotionColorReference,
  rgba: RgbaColor
): unknown {
  const channels = [
    roundColorChannel(rgba[0]),
    roundColorChannel(rgba[1]),
    roundColorChannel(rgba[2]),
  ];

  if (Array.isArray(reference.rawValue)) {
    return reference.rawValue.length > 3
      ? [...channels, roundColorChannel(rgba[3])]
      : channels;
  }

  if (typeof reference.rawValue === "string") {
    const colorBytes = `${toColorByte(rgba[0])}, ${toColorByte(
      rgba[1]
    )}, ${toColorByte(rgba[2])}`;
    const originalValue = reference.rawValue.trim();

    if (/^rgba\(/i.test(originalValue)) {
      return `rgba(${colorBytes}, ${roundColorChannel(rgba[3])})`;
    }

    if (/^rgb\(/i.test(originalValue)) {
      return `rgb(${colorBytes})`;
    }

    const hexValue = originalValue.replace(/^#/, "");
    return rgbaArrToHexStr(
      rgba,
      hexValue.length === 4 || hexValue.length === 8
    );
  }

  return channels;
}

function replaceGradientReferenceColor(
  layer: unknown,
  reference: MotionColorReference,
  rgba: RgbaColor
): boolean {
  const segments = getPathSegments(reference.path);
  const channelKey = segments.pop();
  const colorIndex =
    typeof channelKey === "string" ? Number(channelKey) : Number.NaN;
  if (!Number.isInteger(colorIndex)) {
    return false;
  }

  const vector = getPathValue(layer, segments.join("."));
  if (!Array.isArray(vector)) {
    return false;
  }

  const channels = [
    roundColorChannel(rgba[0]),
    roundColorChannel(rgba[1]),
    roundColorChannel(rgba[2]),
  ];
  let hasChanges = false;

  channels.forEach((channel, offset) => {
    const index = colorIndex + offset;
    if (vector[index] !== channel) {
      vector[index] = channel;
      hasChanges = true;
    }
  });

  return hasChanges;
}

type DocumentColorFallbackResult = {
  changed: boolean;
  reason?: string;
  missingReferences: Array<{
    layerPath: string;
    path: string;
    property: string;
  }>;
};

function replaceDocumentColorReferences(
  workingAnimation: unknown,
  color: DocumentColor,
  to: MotionColorInput
): DocumentColorFallbackResult {
  const replacement = parseDocumentReplacementColor(to);
  if (!replacement) {
    return {
      changed: false,
      reason: "Invalid replacement color.",
      missingReferences: [],
    };
  }

  let changed = false;
  const missingReferences: DocumentColorFallbackResult["missingReferences"] = [];

  color.references.forEach((reference) => {
    const layer = getPathValue(workingAnimation, reference.layerPath);
    const referenceChanged = reference.gradientStop
      ? replaceGradientReferenceColor(layer, reference, replacement)
      : setPathValue(
          layer,
          reference.path,
          formatDocumentColorReplacement(reference, replacement)
        );

    if (!referenceChanged) {
      missingReferences.push({
        layerPath: reference.layerPath,
        path: reference.path,
        property: reference.property,
      });
      return;
    }

    changed = true;
  });

  return {
    changed,
    missingReferences,
  };
}

function logDocumentColorReplacementNoop(
  color: DocumentColor,
  to: MotionColorInput,
  apiBlockedReason: string | undefined,
  fallbackResult: DocumentColorFallbackResult
): void {
  console.warn(
    "[LayerVisualProperties] document color replacement did not apply",
    {
      from: color.color.hex,
      to,
      colorId: color.id,
      referenceCount: color.references.length,
      apiBlockedReason,
      fallbackReason: fallbackResult.reason,
      missingReferences: fallbackResult.missingReferences,
    }
  );
}

function isTextColorField(field: VisualEditableField): boolean {
  return (
    field.property === "textFillColor" || field.property === "textStrokeColor"
  );
}

function cloneJsonValue<T>(value: T): T {
  if (typeof value === "undefined") {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function getEditableTargetFields(
  field: VisualEditableField
): VisualEditableField[] {
  if (field.isMixedValue) {
    return [];
  }

  return field.targetFields?.length ? field.targetFields : [field];
}

function setTextDocumentColorByPath(
  workingAnimation: unknown,
  field: VisualEditableField,
  value: unknown
): boolean {
  const path =
    getPendingFieldPath(field) ?? getTextDocumentMutationProperty(field);
  const match = TEXT_DOCUMENT_COLOR_PATH_PATTERN.exec(path);
  if (!match) {
    return false;
  }

  const resolvedTarget = motionAPI.inspect.resolveTarget(
    workingAnimation as any,
    buildFieldTarget(field)
  )[0];
  const textDocuments = (resolvedTarget?.layer as
    | { t?: { d?: { k?: Array<{ s?: Record<string, unknown> }> } } }
    | undefined)?.t?.d?.k;
  const documentIndex = Number(match[1]);
  const colorKey = match[2];
  const style = textDocuments?.[documentIndex]?.s;

  if (!style || typeof style !== "object") {
    return false;
  }

  const before = JSON.stringify({
    color: style[colorKey],
    strokeWidth: style.sw,
  });

  style[colorKey] = cloneJsonValue(value);
  if (colorKey === "sc" && style.sw == null) {
    style.sw = 1;
  }

  return (
    before !==
    JSON.stringify({
      color: style[colorKey],
      strokeWidth: style.sw,
    })
  );
}

export function useLayerVisualPropertyActions({
  getCurrentFrame,
  target,
  targets,
}: UseLayerVisualPropertyActionsConfig) {
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const applyFieldValue = useCallback(
    (field: VisualEditableField, value: unknown) => {
      const targetFields = getEditableTargetFields(field);
      if (!targetFields.length) {
        return;
      }

      if (isPendingField(field)) {
        applyAnimationJsonEdit((workingAnimation) => {
          let hasChanges = false;

          targetFields.forEach((targetField) => {
            if (targetField.property === "fillColor") {
              const applied = motionAPI.apply.addFill(
                workingAnimation as any,
                buildFieldTarget(targetField),
                getPendingShapeFillOptions(value)
              );
              hasChanges = hasChanges || applied.changes.length > 0;
              return;
            }

            if (targetField.property === "strokeColor") {
              const applied = motionAPI.apply.addOutline(
                workingAnimation as any,
                buildFieldTarget(targetField),
                getPendingShapeOutlineOptions(value)
              );
              hasChanges = hasChanges || applied.changes.length > 0;
              return;
            }

            if (targetField.property === "textFillColor") {
              hasChanges =
                setTextDocumentColorByPath(
                  workingAnimation,
                  targetField,
                  value
                ) || hasChanges;
              return;
            }

            if (targetField.property === "textStrokeColor") {
              hasChanges =
                setTextDocumentColorByPath(
                  workingAnimation,
                  targetField,
                  value
                ) || hasChanges;
            }
          });

          return hasChanges;
        });
        return;
      }

      if (isTextColorField(field)) {
        applyAnimationJsonEdit((workingAnimation) => {
          let hasChanges = false;
          targetFields.forEach((targetField) => {
            const applied = motionAPI.apply.setPropertyValue(
              workingAnimation as any,
              buildFieldTarget(targetField),
              getTextDocumentMutationProperty(targetField),
              value as any
            );

            if (applied.changes.length) {
              hasChanges = true;
              return;
            }

            hasChanges =
              setTextDocumentColorByPath(
                workingAnimation,
                targetField,
                value
              ) || hasChanges;
          });

          return hasChanges;
        });
        return;
      }

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        targetFields.forEach((targetField) => {
          const applied = motionAPI.apply.setPropertyValue(
            workingAnimation as any,
            buildFieldTarget(targetField),
            getTextDocumentMutationProperty(targetField),
            value as any
          );

          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit]
  );

  const removeFieldValue = useCallback<RemoveFieldValue>(
    (field) => {
      const targetFields = getEditableTargetFields(field);
      if (!targetFields.length) {
        return;
      }

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;

        targetFields.forEach((targetField) => {
          const resolvedTarget = motionAPI.inspect.resolveTarget(
            workingAnimation as any,
            buildFieldTarget(targetField)
          )[0];

          if (!resolvedTarget) {
            return;
          }

          if (
            targetField.property === "fillColor" ||
            targetField.property === "strokeColor"
          ) {
            hasChanges =
              removeShapeStyleItem(resolvedTarget.layer, targetField) ||
              hasChanges;
            return;
          }

          if (
            targetField.property === "textFillColor" ||
            targetField.property === "textStrokeColor"
          ) {
            const applied = motionAPI.apply.removePropertyValue(
              workingAnimation as any,
              buildFieldTarget(targetField),
              targetField.property
            );
            hasChanges = hasChanges || applied.changes.length > 0;
          }
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit]
  );

  const applyAnchorPosition = useCallback<ApplyAnchorPosition>(
    (field, position) => {
      const targetFields = getEditableTargetFields(field);
      if (!targetFields.length) {
        return;
      }

      const config: MotionAnchorPositionConfig = {
        position,
        frame: getCurrentFrame(),
      };

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        targetFields.forEach((targetField) => {
          const applied = motionAPI.apply.updateAnchorPosition(
            workingAnimation as any,
            buildFieldTarget(targetField),
            config
          );
          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit, getCurrentFrame]
  );

  const alignLayer = useCallback(
    (config: MotionAlignmentConfig) => {
      const actionTargets = targets?.length ? targets : target ? [target] : [];
      if (!actionTargets.length) return;

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        actionTargets.forEach((actionTarget) => {
          const applied = motionAPI.apply.alignLayer(
            workingAnimation as any,
            actionTarget,
            {
              ...config,
              frame: getCurrentFrame(),
            }
          );
          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit, getCurrentFrame, target, targets]
  );

  const replaceMedia = useCallback<ReplaceMedia>(
    (config) => {
      const actionTargets = targets?.length ? targets : target ? [target] : [];
      if (!actionTargets.length) return;

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        actionTargets.forEach((actionTarget) => {
          const applied = motionAPI.apply.replaceMedia(
            workingAnimation as any,
            actionTarget,
            config
          );
          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit, target, targets]
  );

  const replaceLayerFont = useCallback<ApplyLayerFont>(
    (replacement) => {
      const actionTargets = targets?.length ? targets : target ? [target] : [];
      if (!actionTargets.length) return;

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        actionTargets.forEach((actionTarget) => {
          const applied = motionAPI.apply.replaceLayerFont(
            workingAnimation as any,
            actionTarget,
            replacement
          );
          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit, target, targets]
  );

  const applyDocumentSize = useCallback<ApplyDocumentSize>(
    (dimension, value) => {
      const path = DOCUMENT_SIZE_FIELD_BY_DIMENSION[dimension];
      const nextValue = Math.max(1, Math.round(value));

      applyAnimationJsonEdit((workingAnimation) => {
        const animation = workingAnimation as Record<string, unknown>;
        if (animation[path] === nextValue) {
          return false;
        }

        animation[path] = nextValue;
        return true;
      });
    },
    [applyAnimationJsonEdit]
  );

  const applyDocumentDuration = useCallback<ApplyDocumentDuration>(
    (durationFrames) => {
      const nextDuration = Math.max(
        MIN_SCENE_DURATION_FRAMES,
        Math.round(durationFrames)
      );

      applyAnimationJsonEdit((workingAnimation) => {
        const timing = motionAPI.inspect.getCompositionTiming(
          workingAnimation as any
        );
        const nextEndFrame = (timing?.ip ?? 0) + nextDuration;
        const applied = motionAPI.apply.updateCompositionTiming(
          workingAnimation as any,
          { op: nextEndFrame }
        );

        return applied.changes.length ? applied : false;
      });
    },
    [applyAnimationJsonEdit]
  );

  const replaceDocumentColor = useCallback<ReplaceDocumentColor>(
    (color, to) => {
      applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.replaceDocumentColor(
            workingAnimation as any,
            color.color.hex as any,
            to as any
          );

          if (applied.changes.length) {
            return applied;
          }

          const fallbackResult = replaceDocumentColorReferences(
            workingAnimation,
            color,
            to
          );
          if (fallbackResult.changed) {
            return true;
          }

          logDocumentColorReplacementNoop(
            color,
            to,
            applied.blockedReason,
            fallbackResult
          );
          return false;
        },
        {
          playerUpdate: {
            forceRecreate: true,
            startFrame: getCurrentFrame(),
          },
        }
      );
    },
    [applyAnimationJsonEdit, getCurrentFrame]
  );

  const replaceDocumentMedia = useCallback<ReplaceDocumentMedia>(
    (asset, config) => {
      applyAnimationJsonEdit((workingAnimation) => {
        const applied = motionAPI.apply.replaceMedia(
          workingAnimation as any,
          { path: asset.layerPath },
          config
        );

        return applied.changes.length ? applied : false;
      });
    },
    [applyAnimationJsonEdit]
  );

  const freezeAnimatedFields = useCallback(
    (fields: VisualEditableField[]) => {
      const animatedFields = fields
        .filter((field) => field.isAnimated)
        .flatMap(getEditableTargetFields)
        .filter((field) => field.isAnimated);
      if (!animatedFields.length) return;

      applyAnimationJsonEdit((workingAnimation) => {
        let hasChanges = false;
        animatedFields.forEach((field) => {
          const applied = motionAPI.apply.setPropertyValue(
            workingAnimation as any,
            buildFieldTarget(field),
            getTextDocumentMutationProperty(field),
            field.value as any
          );
          hasChanges = hasChanges || applied.changes.length > 0;
        });

        return hasChanges;
      });
    },
    [applyAnimationJsonEdit]
  );

  return {
    applyFieldValue,
    removeFieldValue,
    freezeAnimatedFields,
    alignLayer,
    applyAnchorPosition,
    replaceMedia,
    replaceLayerFont,
    applyDocumentSize,
    applyDocumentDuration,
    replaceDocumentColor,
    replaceDocumentMedia,
  };
}
